import { HttpClient, HttpErrorResponse, HttpEvent, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { from, map, Observable, of } from "rxjs";
import { MessageService } from "primeng/api";
import * as signalR from "@microsoft/signalr";

import { SettingsSectionsName } from "./settings-sections-name";
import { HeadLoader, StylesData } from "@bbwt/utils/head-loader";
import { BaseDataService, HttpResponsesHandlersFactory } from "@bbwt/modules/data-service";
import { Message } from "@bbwt/classes";
import { CurrentUserChangedData, UserService } from "../users";
import { OrganizationService, IOrganization } from "../companies";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { SettingsSection } from "./classes/settings-section";
import { ProjectSettings, Theme } from "./classes/project-settings";
import { MaintenanceSettings } from "./classes/maintenance-settings";
import { AppStorage } from "@bbwt/utils/app-storage";
import { CookieService } from "@main/cookie/cookie.service";
import { SnomedSettings } from "./classes/snomed-settings";
import { NhsSettings } from "./classes/nhs-settings";
import { LogEventLevelEnum } from "./components/logging/log-event-level-enum";
import { IVictoriaLogsSettings } from "@main/victoria-logs/interfaces/victoria-logs-settings";


@Injectable({
    providedIn: "root"
})
export class SystemConfigurationService extends BaseDataService {
    static readonly MaintenanceSettingsChangedEventName = "MaintenanceSettingsChanged";
    static readonly SettingsSectionChangedEventName = "SettingsSectionChanged";
    static readonly ThemeChangedEventName = "ThemeChanged";

    private readonly systemConfigurationKey = "system-configuration";

    private _settings = AppStorage.getItem<SettingsSection[]>(this.systemConfigurationKey);
    private _hubConnection: signalR.HubConnection;
    private _loadingPromise: Promise<SettingsSection[]>;
    private _loading = false;
    private _logoImageUrl: string;
    private _theme: Theme;

    readonly url = "api/system-configuration";

    constructor(
        http: HttpClient,
        handlersFactory: HttpResponsesHandlersFactory,
        private userService: UserService,
        private messageService: MessageService,
        private broadcastService: BroadcastService
    ) {
        super(http, handlersFactory);

        this.registerSubscriptions();
    }

    get logoImageUrl(): string {
        return this._logoImageUrl;
    }
    get theme(): Theme {
        return this._theme;
    }
    get pwaEnabled(): boolean {
        return CookieService.GetCookie("PwaEnabled") === "True";
    }

    get snomedSettings() {
        return this.getSettingsSection<SnomedSettings>(SettingsSectionsName.SnomedSettings);
    }

    async initialize(): Promise<SettingsSection[]> {
        if (this._settings) {
            const projectSettings = this.getSettingsSectionByName(SettingsSectionsName.ProjectSettings);
            if (projectSettings) {
                const theme = ProjectSettings.GetThemeByCode((<ProjectSettings>projectSettings.value).themeCode);
                if (theme) this.changeCurrentTheme(theme);
            }
        }

        await this.loadSettings();

        this._hubConnection = new signalR.HubConnectionBuilder()
            .withUrl("/api/maintenance")
            .configureLogging(signalR.LogLevel.None)
            .build();

        this._hubConnection.on("InfoUpdated", (data: MaintenanceSettings) => {
            this.broadcastService.broadcast(SystemConfigurationService.MaintenanceSettingsChangedEventName, data);
        });

        this._hubConnection.start().catch((response: signalR.HttpError) => {
            if (response.statusCode >= 500) {
                const interval = setInterval(() => this._hubConnection.start().then(() => clearInterval(interval)), 10000);
            }
        });

        return this._settings;
    }

    getSettingsSection<T>(sectionName: SettingsSectionsName): T {
        const settingsSection = this.getSettingsSectionByName(sectionName);
        return settingsSection ? (settingsSection.value as T) : null;
    }

    get ensureSettingsNotLoading$(): Observable<void> {
        if (this._loadingPromise) {
            return from(this._loadingPromise).pipe(map(_ => null));
        }

        return of(null);
    }

    saveSettings(...settings: SettingsSection[]): Promise<void> {
        return this.handleRequest<any>(this.http.post<any>(this.url, settings), this.handlersFactory.getDefault()).then(
            (settingsResult: SettingsSection[]) => {
                settingsResult.forEach(settingsSectionItem => {
                    const oldSettingsSectionIndex = this._settings.findIndex(
                        oldSettingsSectionItem => oldSettingsSectionItem.sectionName == settingsSectionItem.sectionName
                    );
                    if (oldSettingsSectionIndex >= 0) {
                        this._settings.splice(oldSettingsSectionIndex, 1, settingsSectionItem);
                    }

                    if (settingsSectionItem.sectionName == SettingsSectionsName.ProjectSettings) {
                        this.refreshBranding();
                    }

                    this.broadcastService.broadcast(SystemConfigurationService.SettingsSectionChangedEventName, settingsSectionItem);
                });

                this.messageService.add(Message.Success("The settings have been saved successfully.", "System Configuration"));

                AppStorage.setItem(this.systemConfigurationKey, this._settings);
            }
        );
    }

    setLoadingTimeSettings(settings): Promise<any> {
        return this.handleRequest<any>(
            this.http.post<any>(`${this.url}/loading-times-settings`, settings),
            this.handlersFactory.getDefault()
        );
    }

    setThemeCode(themeCode: string): void {
        const projectSettingsSection = this._settings.find(
            settingsItem => settingsItem.sectionName == SettingsSectionsName.ProjectSettings
        );
        if (projectSettingsSection) {
            projectSettingsSection.value.themeCode = themeCode;

            const projectSettingsSectionCopy = { ...projectSettingsSection };
            projectSettingsSectionCopy.value = { ...projectSettingsSection.value };
            projectSettingsSectionCopy.value.logoIcon = null;
            projectSettingsSectionCopy.value.logoImage = null;

            this.saveSettings(projectSettingsSectionCopy);
        }
    }

    uploadLogoImage(formData: FormData): Observable<HttpEvent<any>> {
        return this.http.request(new HttpRequest("POST", `${this.url}/upload-logo-image`, formData, { reportProgress: true }));
    }

    uploadLogoIcon(formData: FormData): Observable<HttpEvent<any>> {
        return this.http.request(new HttpRequest("POST", `${this.url}/upload-logo-icon`, formData, { reportProgress: true }));
    }

    getEmailsSettings(): Promise<any> {
        return this.handleRequest(this.http.get(`${this.url}/email-settings`), null);
    }

    sendTestEmail() {
        return this.handleRequest(this.http.get(`${this.url}/test-email`), null);
    }

    getNhsSettings(): Promise<NhsSettings> {
        return this.handleRequest<NhsSettings>(
            this.http.get<NhsSettings>(`${this.url}/nhs-settings`),
            this.handlersFactory.getForReadAll());
    }
    getVictoriaLogsSettings(): Promise<IVictoriaLogsSettings> {
        return this.handleRequest(this.http.get(`${this.url}/victoria-logs-settings`), null) as Promise<IVictoriaLogsSettings>;
    }


    private registerSubscriptions() {
        this.broadcastService.on<CurrentUserChangedData>(UserService.CurrentUserChangedEventName).subscribe(data => {
            if (data.isLogin || data.isLogout) {
                this.loadSettings();
            }
        });

        this.broadcastService.on<IOrganization>(OrganizationService.OrganizationChangedEventName).subscribe(company => {
            if (this.userService.isLogged && this.userService.currentUser.organizationId == company.id) {
                this.refreshBranding();
            }
        });
    }

    private async loadSettings(): Promise<SettingsSection[]> {
        if (!this._loading) {
            this._loading = true;

            this._loadingPromise = this.handleRequest<SettingsSection[]>(
                this.http.get<SettingsSection[]>(this.url),
                this.handlersFactory.getDefault()
            );
            this._settings = await this._loadingPromise;

            AppStorage.setItem(this.systemConfigurationKey, this._settings);

            this.refreshBranding();

            this._settings.forEach(settingsSectionItem =>
                this.broadcastService.broadcast(SystemConfigurationService.SettingsSectionChangedEventName, settingsSectionItem)
            );

            this._loading = false;
            this._loadingPromise = null;
        }

        return this._settings;
    }

    private getSettingsSectionByName(sectionName: string): SettingsSection {
        return this._settings.find(settingsSectionItem => settingsSectionItem.sectionName == sectionName);
    }

    private refreshBranding(): void {
        this.refreshThemeLinks();
        this.refreshProjectLogo();
    }

    private changeCurrentTheme(theme: Theme): void {
        this._theme = theme;
        this.setCurrentThemeLinks();

        this.broadcastService.broadcast(SystemConfigurationService.ThemeChangedEventName, this._theme);
    }

    private refreshThemeLinks(): void {
        const projectSettings = this.getSettingsSectionByName(SettingsSectionsName.ProjectSettings).value as ProjectSettings;
        const userBranding = null;

        const newThemeCode = userBranding && !userBranding.disabled && userBranding.theme ? userBranding.theme : projectSettings.themeCode;

        if (newThemeCode == this._theme?.code) return;

        const theme = ProjectSettings.GetThemeByCode(newThemeCode);
        if (theme && theme.code != this._theme?.code) {
            if (!this._theme || theme.template == this._theme.template) {
                this.changeCurrentTheme(theme);
            } else {
                // Change the theme in a storage
                const currentProjectSettings = this.getSettingsSectionByName(SettingsSectionsName.ProjectSettings)
                    ?.value as ProjectSettings;
                currentProjectSettings.themeCode = theme.code;
                AppStorage.setItem(this.systemConfigurationKey, this._settings);

                // Reload the app to change the template
                location.reload();
            }
        }
    }

    private setCurrentThemeLinks(): void {
        if (!this._theme) return;

        HeadLoader.loadStyles(<StylesData>{
            id: "layout-css",
            href: this._theme.layoutFileUrl,
            splashScreenColor: this._theme.primaryColor ?? "#673AB7"
        });
        HeadLoader.loadStyles(<StylesData>{
            id: "theme-css",
            href: this._theme.themeFileUrl,
            splashScreenColor: this._theme.primaryColor ?? "#673AB7"
        });
    }

    private refreshProjectLogo(): void {
        const projectSettings = this.getSettingsSectionByName(SettingsSectionsName.ProjectSettings).value as ProjectSettings;
        const userBranding = null;

        if (projectSettings.logoIcon && projectSettings.logoIcon.id) {
            projectSettings.logoIcon.id = projectSettings.logoIcon.id_original;
        }
        if (projectSettings.logoImage && projectSettings.logoImage.id) {
            projectSettings.logoImage.id = projectSettings.logoImage.id_original;
        }

        // Icon
        const newIconLink =
            userBranding && !userBranding.disabled && userBranding.logoIcon ? userBranding.logoIcon.url : projectSettings.logoIcon?.url;
        const iconLinkTag = document.getElementById("icon-link");
        if (iconLinkTag && iconLinkTag.getAttribute("href") != newIconLink) {
            iconLinkTag.setAttribute("href", newIconLink);
        }

        // Logo Image
        const newLogoImage =
            userBranding && !userBranding.disabled && userBranding.logoImage ? userBranding.logoImage.url : projectSettings.logoImage?.url;
        if (newLogoImage != this._logoImageUrl) this._logoImageUrl = newLogoImage;
    }

    private setLogo(logoIconUrl: string, logoImageUrl: string): void {
        const iconLinkTag = document.getElementById("icon-link");
        if (iconLinkTag && iconLinkTag.getAttribute("href") != logoIconUrl) {
            iconLinkTag.setAttribute("href", logoIconUrl);
        }

        if (logoImageUrl != this._logoImageUrl) this._logoImageUrl = logoImageUrl;
    }

    writeTestLog(eventLevel: LogEventLevelEnum): Promise<any> {
        return this.handleRequest(this.http.post(
            `${this.url}/write-test-log/${eventLevel}`,
            {}),
            {
                handleSuccess: () => this.handlersFactory.getBypass(),
                handleError: response => {
                    this.messageService.add(Message.Error(response.message));
                    return false;
                }
            }
        );
    }

    writeTestLogWithFields(): Promise<any> {
        return this.handleRequest(this.http.post(
            `${this.url}/write-test-log-with-fields`,
            {}),
            {
                handleSuccess: () => this.handlersFactory.getBypass(),
                handleError: response => {
                    this.messageService.add(Message.Error(response.message));
                    return false;
                }
            }
        );
    }

    writeTestLogJson(): Promise<any> {
        return this.handleRequest(this.http.post(
            `${this.url}/write-test-log-json`,
            {}),
            {
                handleSuccess: () => this.handlersFactory.getBypass(),
                handleError: response => {
                    this.messageService.add(Message.Error(response.message));
                    return false;
                }
            }
        );
    }

    writeTestBulkLogs(): Promise<any> {
        return this.handleRequest(this.http.post(
            `${this.url}/write-test-bulk-logs`,
            {}),
            {
                handleSuccess: () => this.handlersFactory.getBypass(),
                handleError: response => {
                    this.messageService.add(Message.Error(response.message));
                    return false;
                }
            }
        );
    }

    writeTestLogsInBlock(): Promise<any> {
        return this.handleRequest(this.http.post(
            `${this.url}/write-test-logs-in-block`,
            {}),
            {
                handleSuccess: () => this.handlersFactory.getBypass(),
                handleError: response => {
                    this.messageService.add(Message.Error(response.message));
                    return false;
                }
            }
        );
    }

}
